import CouponModel from "#src/models/CouponModel.ts";
import GroupRegistrationModel, {
  isGroupRegistration,
} from "#src/models/GroupRegistrationModel.ts";
import { InvoiceItemModel } from "#src/models/InvoiceItemModel.ts";
import MembershipModel, { isMembership } from "#src/models/MembershipModel.ts";
import PrivateSerieModel, {
  isPrivateSerie,
} from "#src/models/PrivateSerieModel.ts";
import calculateInvoiceItemBalance from "#src/utils/calculateInvoiceItemBalance.ts";
import { calculatePricingForMembership } from "#src/utils/calculatePricingForMembership.ts";
import { calculatePricingForPrivateSerie } from "#src/utils/calculatePricingForPrivateSerie.ts";
import calculateProratedRegistrationPrice from "#src/utils/calculateProratedRegistrationPrice.ts";
import { checkCouponData } from "#src/utils/checkCouponData.ts";
import { moment } from "#src/utils/dateTime.ts";
import {
  AmountType,
  CouponPayload,
  Frequency,
  GroupRegistrationStatus,
} from "#types/code-generator.ts";

interface CalculateCouponsForRegistrationInput {
  coupons: CouponPayload[];
  registration: GroupRegistrationModel | PrivateSerieModel | MembershipModel;
  startDate: Date;
  organization?: {
    departments: {
      id: string;
    }[];
    timezone: string;
  };
  invoiceItem?: InvoiceItemModel;
}

export default ({
  coupons,
  registration,
  startDate,
  organization,
  invoiceItem,
}: CalculateCouponsForRegistrationInput) => {
  const result: Array<{
    coupon: CouponModel;
    amount: number;
  }> = [];

  const activeCoupons = coupons.filter((c) =>
    checkCouponData({
      coupon: c,
    })
  );

  // console.log("activeCoupons", activeCoupons);

  let offering;
  let offeringPrice;

  if (isGroupRegistration(registration)) {
    offering = registration.class;

    if (invoiceItem) {
      offeringPrice = calculateInvoiceItemBalance(invoiceItem).totalOwed;
    } else {
      offeringPrice = calculateProratedRegistrationPrice({
        classItem: registration.class,
        endDate: moment(startDate).endOf("month").toDate(),
        startDate,
        organization,
      });
    }
  } else if (isPrivateSerie(registration)) {
    offering = registration.offering;
    offeringPrice = calculatePricingForPrivateSerie({
      privateSerie: registration,
      organization,
    }).price;
  } else if (isMembership(registration)) {
    offering = registration.offering;
    offeringPrice = calculatePricingForMembership({
      membership: registration,
      startDate,
    }).price;
  }

  activeCoupons.forEach((coupon) => {
    let amount = 0;

    if (
      coupon.availableForAllOfferings ||
      (offering.coupons &&
        offering.coupons.find((ocoupon) => ocoupon.id === coupon.id))
    ) {
      if (coupon.amountType === AmountType.Dollar) {
        amount += coupon.amount;
      } else {
        amount += offeringPrice * (coupon.amount / 100);
      }

      if (
        registration.status === GroupRegistrationStatus.Waitlist &&
        moment().isSame(startDate, "day")
      ) {
        amount = 0;
      }

      if (
        coupon.frequency === Frequency.OneTime &&
        moment().isBefore(startDate, "day")
      ) {
        amount = 0;
      }

      if (
        coupon.frequency === Frequency.Monthly &&
        coupon.applyAtCheckout === false
      ) {
        amount = 0;
      }

      if (amount >= offeringPrice) {
        amount = offeringPrice;
      }

      if (!Number.isInteger(amount)) {
        amount = Math.floor(amount);
      }

      result.push({ coupon, amount });
    }
  });

  // console.log("result", result);

  return result;
};
